/** IT-работы, чистые правила карточки: сводка «Согласовано N из M», последний «было / стало», ФИО людей заявки. */
import type { EdoAuditEvent, ItWorksLineState, RequestCard } from '../../../lib/types'

/** «Согласовано N из M»: исключённые строки в раунд не входят и не считаются ни в N, ни в M. */
export function itWorksSummary(lines: ItWorksLineState[]): { approved: number; total: number } {
  const live = lines.filter((l) => l.status !== 'excluded')
  return { approved: live.filter((l) => l.status === 'approved').length, total: live.length }
}

type Diff = NonNullable<EdoAuditEvent['diff']>

/** «Было / стало» из последнего события `revised`, где есть хотя бы один из ключей, — только эти ключи; иначе null. */
export function lastRevisedDiff(events: EdoAuditEvent[] | undefined, keys: string[]): Diff | null {
  const has = (d: Diff) => keys.filter((k) => k in d)
  const ev = [...(events ?? [])].reverse().find((e) => e.event === 'revised' && e.diff && has(e.diff).length)
  return ev?.diff ? Object.fromEntries(has(ev.diff).map((k) => [k, ev.diff![k]])) : null
}

/**
 * id → ФИО из того, что карточка уже несёт: адресаты и решившие задач, люди маршрута. В `itWorks`
 * имён согласующих строк нет (R54) — кого не нашли, показываем прочерком.
 */
export function itWorksNames(card: RequestCard): Map<string, string> {
  const names = new Map<string, string>()
  for (const tk of card.tasks ?? []) for (const p of [tk.assignee, tk.decisionBy]) if (p) names.set(p.id, p.fullName)
  for (const r of card.route ?? []) if (r.person) names.set(r.person.id, r.person.fullName)
  return names
}

export interface ReworkNote { comment: string; by?: string }

/**
 * Почему заявку вернули — для формы доработки: комментарии строк «На доработку» (lineId → текст и кто
 * решил) и комментарий финального утверждающего, если возврат пришёл с финала. Задачи приходят по
 * createdAt: возврат с финала — задача прямо перед открытой REWORK, это IT_FINAL в статусе REWORK.
 */
export function reworkNotes(card: RequestCard): { lines: Map<string, ReworkNote>; final: ReworkNote | null } {
  const names = itWorksNames(card)
  const lines = new Map<string, ReworkNote>()
  for (const l of card.itWorks?.lines ?? []) {
    if (l.status === 'rework' && l.decision?.comment) lines.set(l.lineId, { comment: l.decision.comment, by: names.get(l.decision.approverId) })
  }
  const tasks = card.tasks ?? []
  const at = tasks.findIndex((tk) => tk.stage === 'REWORK' && String(tk.status) === 'OPEN')
  const prev = at > 0 ? tasks[at - 1] : undefined
  const final =
    prev?.stage === 'IT_FINAL' && String(prev.status) === 'REWORK' && prev.decisionComment
      ? { comment: prev.decisionComment, by: (prev.decisionBy ?? prev.assignee)?.fullName }
      : null
  return { lines, final }
}
